import { NavLink, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import {
  LayoutDashboard,
  FileText,
  FileCheck,
  FileStack,
  BookOpen,
  Settings,
  ChevronDown,
  X,
  Menu,
} from 'lucide-react'
import { useState } from 'react'

interface NavItem {
  label: string
  path: string
  icon: React.ComponentType<{ className?: string }>
  children?: Array<{
    label: string
    path: string
  }>
}

const navItems: NavItem[] = [
  {
    label: 'Inicio',
    path: '/',
    icon: LayoutDashboard,
  },
  {
    label: 'Ordenes de Contrato',
    path: '/intakes',
    icon: FileText,
    children: [
      { label: 'Nueva Orden', path: '/intakes/new' },
      { label: 'Lista de Ordenes', path: '/intakes' },
    ],
  },
  {
    label: 'Contratos',
    path: '/contracts',
    icon: FileCheck,
  },
  {
    label: 'Plantillas',
    path: '/templates',
    icon: FileStack,
  },
  {
    label: 'Clausulas',
    path: '/clauses',
    icon: BookOpen,
  },
  {
    label: 'Ajustes',
    path: '/settings',
    icon: Settings,
  },
]

interface SidebarProps {
  open: boolean
  onClose: () => void
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const location = useLocation()
  const [expandedItems, setExpandedItems] = useState<string[]>(['/intakes'])

  const toggleExpanded = (path: string) => {
    setExpandedItems((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    )
  }

  const isActive = (path: string, children?: NavItem['children']) => {
    if (children) {
      return children.some((child) => location.pathname === child.path)
    }
    return location.pathname === path
  }

  return (
    <>
      {/* Mobile overlay */}
      {open && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={onClose} />}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed top-0 left-0 z-50 h-full w-64 bg-sidebar-bg text-sidebar-text',
          'transform transition-transform duration-300 ease-in-out',
          'lg:translate-x-0 lg:static lg:z-0',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">OH</span>
            </div>
            <span className="font-semibold text-lg">Orientamos Hub</span>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-sidebar-hover lg:hidden">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon
            const active = isActive(item.path, item.children)
            const expanded = expandedItems.includes(item.path)

            return (
              <div key={item.path}>
                {item.children ? (
                  <>
                    <button
                      onClick={() => toggleExpanded(item.path)}
                      className={clsx(
                        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                        active
                          ? 'bg-sidebar-active text-white'
                          : 'text-neutral-300 hover:bg-sidebar-hover hover:text-white'
                      )}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <span className="flex-1 text-left">{item.label}</span>
                      <ChevronDown
                        className={clsx('w-4 h-4 transition-transform', expanded && 'rotate-180')}
                      />
                    </button>
                    {expanded && (
                      <div className="mt-1 ml-8 space-y-1">
                        {item.children.map((child) => (
                          <NavLink
                            key={child.path}
                            to={child.path}
                            onClick={onClose}
                            className={({ isActive }) =>
                              clsx(
                                'block px-3 py-2 rounded-lg text-sm transition-colors',
                                isActive
                                  ? 'bg-sidebar-active text-white font-medium'
                                  : 'text-neutral-400 hover:bg-sidebar-hover hover:text-white'
                              )
                            }
                          >
                            {child.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <NavLink
                    to={item.path}
                    onClick={onClose}
                    end={item.path === '/'}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-sidebar-active text-white'
                          : 'text-neutral-300 hover:bg-sidebar-hover hover:text-white'
                      )
                    }
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                )}
              </div>
            )
          })}
        </nav>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-white/10">
          <p className="text-xs text-neutral-400">Orientamos Propiedad Raiz</p>
          <p className="text-xs text-neutral-500">v0.1.0</p>
        </div>
      </aside>
    </>
  )
}

export function SidebarToggle({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="p-2 rounded-lg hover:bg-neutral-100 lg:hidden"
      aria-label="Abrir menu"
    >
      <Menu className="w-5 h-5 text-neutral-700" />
    </button>
  )
}
